import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link as JoyLink,
  Skeleton,
  Stack,
  Typography,
} from "@mui/joy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link as RouterLink } from "react-router-dom";
import PageHeader from "../../../components/PageHeader";
import { useOrgSettingsContext } from "../../../providers/OrgSettingsProvider";
import { useOrganizationDashboardsApi } from "../api";
import { DashboardListResponse } from "../types";
import { DashboardPanel } from "../components/DashboardPanel";

export const ModuleDashboardsOverview: React.FC = () => {
  const { user, organizationDetails } = useOrgSettingsContext();
  const organizationId = user.organization_id ?? "";
  const { listDashboards } = useOrganizationDashboardsApi();
  const [result, setResult] = useState<DashboardListResponse | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (!organizationId)
        throw new Error("Your organization could not be identified.");
      setResult(await listDashboards(organizationId));
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Module dashboards could not be loaded.",
      );
    } finally {
      setLoading(false);
    }
  }, [listDashboards, organizationId]);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <>
      <PageHeader>
        <Typography level="h1">Module dashboards</Typography>
        <Typography level="body-sm" color="neutral">
          {organizationDetails?.name ? `${organizationDetails.name} · ` : ""}
          Operational metrics across your organization’s modules
        </Typography>
      </PageHeader>
      {loading ? (
        <Stack spacing={2} role="status" aria-label="Loading module dashboards">
          <Skeleton height={160} />
          <Skeleton height={160} />
        </Stack>
      ) : error ? (
        <Alert color="danger" role="alert">
          <Stack spacing={1}>
            <Typography>{error}</Typography>
            <Button
              startDecorator={<RefreshIcon />}
              onClick={() => void load()}
            >
              Retry
            </Button>
          </Stack>
        </Alert>
      ) : !result?.modules.length ? (
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-md">
              No module dashboards available
            </Typography>
            <Typography level="body-sm" color="neutral">
              Dashboards will appear here when enabled for your organization.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={4}>
          {result.modules.map((module) => (
            <Box component="section" key={module.id}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
              >
                <Typography level="h2">{module.display_name}</Typography>
                <JoyLink
                  component={RouterLink}
                  to={
                    module.detail_route ||
                    `/organization/settings/dashboards/${encodeURIComponent(module.id)}`
                  }
                >
                  View {module.display_name} dashboard
                </JoyLink>
              </Stack>
              {module.overview_panels.length ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
                    gap: 2,
                  }}
                >
                  {[...module.overview_panels]
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    .map((panel) => (
                      <DashboardPanel
                        key={panel.panel_id}
                        organizationId={organizationId}
                        moduleId={module.id}
                        level="overview"
                        panel={panel}
                      />
                    ))}
                </Box>
              ) : (
                <Typography level="body-sm" color="neutral">
                  No overview panels are configured.
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </>
  );
};
